import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, functionsUsEast1 } from '../utils/init-firebase';
import { supabase, isSupabaseConfigured } from '../utils/supabase';

export function useAriveLoanDetails(deal) {
  const [loanData, setLoanData] = useState(deal?.ariveLoanData || {});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (deal?.ariveLoanData) {
      setLoanData(deal.ariveLoanData);
    }
  }, [deal?.ariveLoanData]);

  const refresh = useCallback(async () => {
    if (!deal?.ariveId) return;
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functionsUsEast1, 'refreshAriveData');
      const result = await fn({ dealId: deal.id });
      if (result.data?.loanData) {
        setLoanData(result.data.loanData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [deal?.id, deal?.ariveId]);

  return { loanData, loading, error, refresh };
}

export function useAriveSearch(email) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    try {
      const fn = httpsCallable(functionsUsEast1, 'searchAriveLoans');
      const result = await fn({ email });
      setResults(result.data?.loans || []);
    } catch (_) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [email]);

  return { results, loading, search };
}

// Unchanged loans are only re-verified by the 4-hourly stale sweep
// (syncStaleAriveLoans), so anything checked within 5h counts as fresh.
const FRESHNESS_STALE_AFTER_MS = 5 * 60 * 60 * 1000;

/**
 * Freshness = "when did Origin last CHECK Arive for this loan", which lives
 * on the mirror row (arive_loans.last_fetched_at -- stamped by every
 * webhook, poll, manual refresh, and the stale sweep).
 *
 * Deliberately NOT deal.arive_last_synced_at: that column is the anti-echo
 * marker and only moves when an inbound apply actually CHANGED the deal, so
 * it goes stale on any loan that simply hasn't changed lately.
 */
export function useAriveSyncStatus(deal) {
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const ariveId = deal?.ariveId ? String(deal.ariveId) : null;

  const refetch = useCallback(async () => {
    if (!ariveId) {
      setLastCheckedAt(null);
      setLoaded(true);
      return;
    }
    try {
      // Service-role path: arive_loans is locked to backend after GA RLS.
      const fn = httpsCallable(functionsUsEast1, 'getAriveFreshness');
      const result = await fn({ ariveLoanId: ariveId });
      setLastCheckedAt(result.data?.lastFetchedAt || result.data?.updatedAt || null);
    } catch (_) {
      // keep the previous value; worst case the badge is briefly wrong
    }
    setLoaded(true);
  }, [ariveId]);

  // deal.updatedAt changes on every inbound apply (and the deal page already
  // subscribes to the deal row), so freshness re-pulls without its own channel.
  useEffect(() => {
    refetch();
  }, [refetch, deal?.updatedAt]);

  let status;
  if (!deal) {
    status = 'none';
  } else if (!ariveId) {
    status = 'unlinked';
  } else if (!loaded) {
    status = 'none'; // don't flash "Stale" while the mirror row is loading
  } else {
    const ts = lastCheckedAt || deal.ariveLastSyncedAt || null;
    if (!ts) {
      status = 'stale'; // linked but never fetched
    } else {
      status = Date.now() - new Date(ts).getTime() < FRESHNESS_STALE_AFTER_MS ? 'synced' : 'stale';
    }
  }

  return { status, lastCheckedAt, refetch };
}

export function useArivePush() {
  const [pushing, setPushing] = useState(false);
  const [pushError, setPushError] = useState(null);

  const pushToArive = useCallback(async (functionName, payload) => {
    setPushing(true);
    setPushError(null);
    try {
      const fn = httpsCallable(functionsUsEast1, functionName);
      const result = await fn(payload);
      return result.data;
    } catch (err) {
      setPushError(err.message);
      throw err;
    } finally {
      setPushing(false);
    }
  }, []);

  return { pushToArive, pushing, pushError };
}

/**
 * Company-sync access hook (payroll specialist model).
 *
 * Reads the single arive_sync_settings row and reports whether the
 * company-wide Arive sync is configured/enabled. Panels and banners on deal
 * pages should gate on THIS (plus deal visibility, which the deal page
 * already enforces) instead of requiring per-user credentials.
 *
 * Settings row contains no secrets (sync user uid + toggles + cursors) and
 * is readable by the frontend anon client via RLS.
 */
export function useAriveAccess() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!isSupabaseConfigured()) { setLoading(false); return; }
      try {
        const { data, error } = await supabase
          .from('arive_sync_settings')
          .select('sync_user_id, inbound_enabled, outbound_enabled, inbound_auto_create_deals, backfill_scope')
          .eq('is_singleton', true)
          .maybeSingle();
        if (!cancelled) setSettings(error ? null : data);
      } catch (_) {
        if (!cancelled) setSettings(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const companySyncConfigured = Boolean(settings?.sync_user_id);
  const companySyncEnabled = companySyncConfigured && settings?.inbound_enabled !== false;

  return { companySyncConfigured, companySyncEnabled, settings, loading };
}

/**
 * Subscribes to users/{uid} and exposes Arive credential status.
 * Mirrors the same check that AriveSettings uses to enable the integration:
 *   hasCredentials = !!users/{uid}.ariveCredentials.clientId
 *   isEnabled      = users/{uid}.ariveEnabled === true
 *
 * Legacy per-user gate: still used by AriveSettings itself and as a
 * fallback surface gate when company sync is not configured.
 */
export function useAriveCredentials(userId) {
  const [hasCredentials, setHasCredentials] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setHasCredentials(false);
      setIsEnabled(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    const userRef = doc(db, 'users', userId);
    const unsubscribe = onSnapshot(
      userRef,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setHasCredentials(!!data?.ariveCredentials?.clientId);
        setIsEnabled(data?.ariveEnabled === true);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsubscribe();
  }, [userId]);

  return { hasCredentials, isEnabled, loading };
}
